// Stripe return page. Reads GET /payments/v1/checkout/status/{session_id}
// and, once paid, the SRU balance from GET /studio/usage/balance.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import PageShell from "@/components/founding/PageShell";
import SeoHead from "@/components/seo/SeoHead";
import {
    Loader2,
    CheckCircle2,
    AlertTriangle,
    ArrowRight,
    Sparkles,
} from "lucide-react";
import { apiBase } from "@/lib/apiBase";
import { getClientId } from "@/lib/clientId";

const API = apiBase();
const SUPPORT = "support@fsr.studio";
// A few tries while the webhook writes the credit row. Not a tight loop.
const POLL_BACKOFF_MS = [1000, 2000, 4000, 8000];

function isPaid(data) {
    return data?.payment_status === "paid" || data?.payment_status === "no_payment_required";
}

function planLabel(status) {
    const raw = status?.plan || status?.package_id || "plan";
    return String(raw).replace(/_/g, " ").toUpperCase();
}

function money(status) {
    const cents = Number(status?.amount_total);
    if (!Number.isFinite(cents)) return null;
    const currency = String(status?.currency || "usd").toUpperCase();
    return `${(cents / 100).toFixed(2)} ${currency}`;
}

function formatSru(balance) {
    if (balance === undefined) return "…";
    if (!balance) return "—";
    if (balance.unlimited) return "Unlimited";
    const value = Number(balance.sru_remaining);
    if (!Number.isFinite(value)) return "—";
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function failureCopy(kind) {
    if (kind === "missing") {
        return `We couldn't find this checkout. If you were charged, email ${SUPPORT} and we'll sort it out.`;
    }
    if (kind === "invalid") {
        return `This return link doesn't look right. Go back to pricing and try again, or email ${SUPPORT}.`;
    }
    return `We couldn't confirm this payment. Please try again, or email ${SUPPORT}.`;
}

export default function PaymentSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [phase, setPhase] = useState(sessionId ? "checking" : "error");
    const [errorKind, setErrorKind] = useState(sessionId ? null : "invalid");
    const [status, setStatus] = useState(null);
    const [balance, setBalance] = useState(undefined);
    const [attempts, setAttempts] = useState(0);
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        if (!sessionId) {
            setPhase("error");
            setErrorKind("invalid");
            return undefined;
        }
        let cancelled = false;
        let timer = null;
        setPhase("checking");
        setErrorKind(null);
        setStatus(null);
        setBalance(undefined);
        setAttempts(0);

        const loadBalance = async () => {
            try {
                const response = await axios.get(`${API}/studio/usage/balance`, {
                    headers: { "X-Client-Id": getClientId() },
                });
                if (!cancelled) setBalance(response.data);
            } catch {
                if (!cancelled) setBalance(null);
            }
        };

        const schedule = (attempt) => {
            const delay = POLL_BACKOFF_MS[attempt - 1];
            if (delay == null) return false;
            timer = setTimeout(() => {
                tick(attempt + 1);
            }, delay);
            return true;
        };

        const tick = async (attempt) => {
            if (cancelled) return;
            setAttempts(attempt);
            try {
                const response = await axios.get(
                    `${API}/payments/v1/checkout/status/${encodeURIComponent(sessionId)}`,
                );
                if (cancelled) return;
                const data = response.data || {};
                setStatus(data);
                const paid = isPaid(data);
                const granted = data.credits_granted === true;
                if (paid && granted) {
                    setPhase("paid");
                    await loadBalance();
                    return;
                }
                if (data.status === "expired") {
                    setPhase("unpaid");
                    return;
                }
                if (paid && !granted) {
                    setPhase("finalizing");
                    if (schedule(attempt)) return;
                    setPhase("paid");
                    await loadBalance();
                    return;
                }
                setPhase("checking");
                if (schedule(attempt)) return;
                setPhase("unpaid");
            } catch (err) {
                if (cancelled) return;
                const code = err?.response?.status;
                if (code === 404) setErrorKind("missing");
                else if (code === 400) setErrorKind("invalid");
                else setErrorKind("failed");
                setPhase("error");
            }
        };

        tick(1);
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [sessionId, retry]);

    const stillFinalizing = phase === "paid" && status?.credits_granted !== true;

    return (
        <PageShell>
            <SeoHead
                title="Payment Confirmation · FSRS"
                description="Confirming your FSRS subscription."
                canonical="/payments/success"
            />
            <section
                className="relative border-b border-white/10 bg-black"
                data-testid="payment-success-page"
            >
                <div className="mx-auto max-w-[900px] px-6 lg:px-10 py-20 lg:py-28">
                    {phase === "error" ? (
                        <ErrorState
                            message={failureCopy(errorKind)}
                            onRetry={() => setRetry((n) => n + 1)}
                        />
                    ) : phase === "unpaid" ? (
                        <UnpaidState onRetry={() => setRetry((n) => n + 1)} />
                    ) : phase === "finalizing" ? (
                        <FinalizingState attempts={attempts} />
                    ) : phase === "paid" ? (
                        <PaidState
                            status={status}
                            balance={balance}
                            stillFinalizing={stillFinalizing}
                        />
                    ) : (
                        <CheckingState attempts={attempts} />
                    )}
                </div>
            </section>
        </PageShell>
    );
}

function CheckingState({ attempts }) {
    return (
        <div className="text-center" data-testid="payment-pending-state">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-amber-400/50 bg-amber-400/[0.08] mb-8">
                <Loader2
                    className="w-7 h-7 text-amber-300 animate-spin"
                    strokeWidth={1.5}
                />
            </div>
            <div className="fsrs-overline mb-4 text-amber-300">
                /&nbsp;CONFIRMING&nbsp;YOUR&nbsp;PURCHASE
            </div>
            <h1 className="fsrs-heading text-white text-3xl sm:text-4xl tracking-tight leading-tight">
                Checking with Stripe…
            </h1>
            <p className="mt-4 fsrs-mono text-slate-400 text-base">
                Attempt&nbsp;{attempts || 1}
            </p>
        </div>
    );
}

function FinalizingState({ attempts }) {
    return (
        <div className="text-center" data-testid="payment-finalizing-state">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-amber-400/50 bg-amber-400/[0.08] mb-8">
                <Loader2
                    className="w-7 h-7 text-amber-300 animate-spin"
                    strokeWidth={1.5}
                />
            </div>
            <div className="fsrs-overline mb-4 text-amber-300">
                /&nbsp;FINALIZING
            </div>
            <h1 className="fsrs-heading text-white text-3xl sm:text-4xl tracking-tight leading-tight">
                Finalizing your credits…
            </h1>
            <p className="mt-4 fsrs-mono text-slate-400 text-base leading-relaxed max-w-xl mx-auto">
                Stripe confirmed the payment. We&rsquo;re finalizing the SRU
                grant and will show your balance as soon as it lands.
                Attempt&nbsp;{attempts}.
            </p>
        </div>
    );
}

function PaidState({ status, balance, stillFinalizing }) {
    const total = money(status);
    return (
        <div className="text-center" data-testid="payment-paid-state">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-emerald-400/60 bg-emerald-400/[0.10] mb-8">
                <CheckCircle2
                    className="w-8 h-8 text-emerald-300"
                    strokeWidth={1.5}
                />
            </div>
            <div className="fsrs-overline mb-4 text-emerald-300">
                /&nbsp;PAYMENT&nbsp;CONFIRMED
            </div>
            <h1 className="fsrs-heading text-white text-4xl sm:text-5xl tracking-tight leading-[0.95]">
                Payment confirmed.
            </h1>
            <p className="mt-4 fsrs-mono text-slate-400 text-base">
                Your&nbsp;
                <span className="text-white">{planLabel(status)}</span>
                &nbsp;subscription is active.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-0 border border-white/10 max-w-2xl mx-auto">
                <Card
                    label="SRU BALANCE"
                    value={formatSru(balance)}
                    accent="emerald"
                    testid="payment-sru-balance"
                />
                <Card
                    label="PLAN"
                    value={planLabel(status)}
                    accent="white"
                    testid="paid-plan-granted"
                />
            </div>
            <p className="mt-6 fsrs-mono text-slate-400 text-base leading-relaxed max-w-xl mx-auto">
                {total ? (
                    <>
                        Charged&nbsp;
                        <span className="text-emerald-300">{total}</span>.
                    </>
                ) : null}{" "}
                {balance === null
                    ? "Your balance will show in Studio. "
                    : "This is the SRU balance on this browser profile. "}
                {stillFinalizing
                    ? `Credits are still finalizing and will update that balance shortly. If they don't, email ${SUPPORT}.`
                    : "You're set to open Studio."}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
                <Link
                    to="/studio"
                    className="fsrs-cta-claim"
                    data-testid="paid-cta-studio"
                >
                    <Sparkles className="w-4 h-4" strokeWidth={2} />
                    Open Studio
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </Link>
            </div>
        </div>
    );
}

function UnpaidState({ onRetry }) {
    return (
        <div className="text-center" data-testid="payment-unpaid-state">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-amber-400/50 bg-amber-400/[0.08] mb-8">
                <AlertTriangle className="w-7 h-7 text-amber-300" strokeWidth={1.5} />
            </div>
            <div className="fsrs-overline mb-4 text-amber-300">
                /&nbsp;PAYMENT&nbsp;NOT&nbsp;COMPLETED
            </div>
            <h1 className="fsrs-heading text-white text-3xl sm:text-4xl tracking-tight leading-tight">
                This checkout isn&rsquo;t paid yet.
            </h1>
            <p className="mt-4 fsrs-mono text-slate-400 text-lg max-w-xl mx-auto">
                If you finished paying, try again in a moment. Otherwise head
                back to pricing, or email {SUPPORT}.
            </p>
            <ActionRow onRetry={onRetry} />
        </div>
    );
}

function ErrorState({ message, onRetry }) {
    return (
        <div className="text-center" data-testid="payment-error-state">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-red-500/60 bg-red-500/[0.08] mb-8">
                <AlertTriangle className="w-7 h-7 text-red-400" strokeWidth={1.5} />
            </div>
            <div className="fsrs-overline mb-4 text-red-400">
                /&nbsp;PAYMENT&nbsp;UNCONFIRMED
            </div>
            <h1 className="fsrs-heading text-white text-3xl sm:text-4xl tracking-tight leading-tight">
                We couldn&rsquo;t confirm this payment.
            </h1>
            <p className="mt-4 fsrs-mono text-slate-400 text-lg max-w-xl mx-auto">
                {message}
            </p>
            <ActionRow onRetry={onRetry} />
        </div>
    );
}

function ActionRow({ onRetry }) {
    return (
        <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button
                type="button"
                onClick={onRetry}
                className="fsrs-cta-ghost text-lg tracking-wider py-3 px-5"
                data-testid="payment-retry"
            >
                TRY AGAIN
            </button>
            <a
                href={`mailto:${SUPPORT}`}
                className="fsrs-cta-ghost text-lg tracking-wider py-3 px-5"
                data-testid="payment-support-link"
            >
                EMAIL {SUPPORT}
            </a>
            <Link
                to="/pricing"
                className="fsrs-cta-ghost text-lg tracking-wider py-3 px-5"
            >
                BACK TO PRICING
            </Link>
        </div>
    );
}

function Card({ label, value, accent, testid }) {
    const toneClass = {
        emerald: "text-emerald-300",
        white: "text-white",
        red: "text-red-400",
    }[accent || "white"];
    return (
        <div
            className="bg-black p-6 lg:p-7 border-r-0 sm:[&:first-child]:border-r border-white/10 border-b sm:border-b-0 last:border-b-0"
            data-testid={testid}
        >
            <div className="fsrs-label text-slate-500 mb-2">{label}</div>
            <div
                className={`fsrs-heading ${toneClass} text-3xl tracking-tight tabular-nums`}
            >
                {value}
            </div>
        </div>
    );
}
